import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchGroupsList, useCreateGroup, useRetryGroupSync } from "./api";
import { waitForGroupReady } from "./bookingFlow";

const IDLE = { phase: "idle", failed: false, error: "", slow: false, rowId: null };

// Runs the whole of "Create group" and tells the form where it is:
// (optionally) upload files -> save the booking -> the WhatsApp group is created
// -> the invitations go out -> the group is in the desk list -> open the desk.
// The advisor is only taken to the desk once the last of those is true; any
// failure stays on screen with a way forward instead of leaving them guessing.
export function useBookingFlow() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const create = useCreateGroup();
  const retrySync = useRetryGroupSync();
  const [state, setState] = useState(IDLE);
  const alive = useRef(true);
  const lastRun = useRef(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const patch = useCallback((p) => alive.current && setState((s) => ({ ...s, ...p })), []);

  const fetchAndCache = useCallback(async () => {
    const data = await fetchGroupsList();
    qc.setQueryData(["groups"], data);
    return data;
  }, [qc]);

  const goToDesk = useCallback(
    (rowId) => navigate("/desk", { state: rowId ? { openRow: rowId } : undefined }),
    [navigate]
  );

  const wait = useCallback(
    async (rowId) => {
      patch({ phase: "creating_group", failed: false, error: "", slow: false });
      const out = await waitForGroupReady({
        fetchGroups: fetchAndCache,
        rowId,
        isCancelled: () => !alive.current,
        onPhase: (phase) => (phase === "creating_group" || phase === "sending_invites") && patch({ phase }),
      });
      if (out.outcome === "ready") {
        patch({ phase: "opening_desk" });
        if (out.warning) toast.warning(out.warning);
        else toast.success("Group created and invitations sent.");
        goToDesk(rowId);
      } else if (out.outcome === "error") {
        patch({ failed: true, error: out.error });
      } else if (out.outcome === "timeout") {
        patch({ slow: true });
      }
    },
    [patch, fetchAndCache, goToDesk]
  );

  // `prepare` (optional) uploads files and returns extra payload fields, for
  // forms that upload at submit. Forms that upload as files are picked skip it.
  const start = useCallback(
    async ({ prepare, payload }) => {
      lastRun.current = { prepare, payload };
      let rowId = null;
      try {
        let extra = {};
        if (prepare) {
          patch({ ...IDLE, phase: "uploading" });
          extra = (await prepare()) || {};
        }
        patch({ ...IDLE, phase: "saving" });
        const data = await create.mutateAsync({ ...payload, ...extra });
        // An older API returns only the list; the newest row is then this booking.
        rowId = data?.row_id || Math.max(0, ...(data?.rows || []).map((r) => r._row || 0)) || null;
        patch({ rowId });
      } catch (err) {
        patch({ failed: true, error: err?.response?.data?.detail || err?.message || "Could not save the booking." });
        return;
      }
      await wait(rowId);
    },
    [create, patch, wait]
  );

  // Try again from the step that failed.
  const retry = useCallback(async () => {
    if (state.rowId) {
      // The booking exists: only the group is stuck, so ask the API to retry it.
      patch({ failed: false, error: "" });
      try {
        await retrySync.mutateAsync(state.rowId);
      } catch (err) {
        patch({ failed: true, error: err?.response?.data?.detail || err?.message || "Retry failed." });
        return;
      }
      await wait(state.rowId);
    } else if (lastRun.current) {
      await start(lastRun.current);
    }
  }, [state.rowId, retrySync, patch, wait, start]);

  const keepWaiting = useCallback(() => state.rowId && wait(state.rowId), [state.rowId, wait]);

  return {
    state,
    busy: state.phase !== "idle" && !state.failed && !state.slow,
    active: state.phase !== "idle",
    start,
    retry,
    keepWaiting,
    goToDesk: () => goToDesk(state.rowId),
    reset: () => setState(IDLE),
  };
}
